// pages/Documents/DocumentsList.tsx - Fixed with clean CSS classes
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  List, 
  FileText, 
  Copy, 
  Upload, 
  Grid, 
  Search, 
  Filter, 
  Calendar, 
  User, 
  MoreHorizontal,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Eye,
  Download,
  Plus,
  ChevronDown,
  FolderOpen,
  Send,
  Settings
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { ROUTES } from '../../constants/routes';
import './DocumentsList.css';

interface Document {
  _id: string;
  filename: string;
  document_type: string;
  upload_date: string;
  processed_date: string;
  status: string;
  page_count?: number;
  ocr_data?: {
    extracted_text: string;
  };
  document_metadata?: {
    project_name?: string;
    owner?: string;
    contractor?: string;
  };
}

const DocumentsList: React.FC = () => {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'tiles'>('tiles');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    window.document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const quickActions = [
    {
      label: 'Upload New Contract',
      icon: Upload,
      action: () => {
        navigate(ROUTES.UPLOAD);
        setIsDropdownOpen(false);
      },
      primary: true
    },
    {
      label: 'View Dashboard',
      icon: Home,
      action: () => {
        navigate(ROUTES.DASHBOARD);
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Export Reports',
      icon: Download,
      action: () => {
        console.log('Export reports clicked');
        setIsDropdownOpen(false);
      }
    },
    {
      label: 'Share Insights',
      icon: Send,
      action: () => {
        console.log('Share insights clicked');
        setIsDropdownOpen(false);
      }
    }
  ];

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('http://localhost:8000/api/documents');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📄 Loaded documents:', data.data?.length || 0);
      setDocuments(data.data || []);
    } catch (err) {
      console.error('❌ Error loading documents:', err);
      setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentClick = (document: Document) => {
    console.log('🎯 Navigating to document analysis:', document._id);
    navigate(`/analysis/${document._id}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getProcessedBy = (document: Document) => {
    return document.document_metadata?.owner || 'Artifi System';
  };

  const getStatusInfo = (status: string) => {
    switch (status.toLowerCase()) {
      case 'enhanced_processed_v3_highlighting':
      case 'enhanced_processed_v2':
      case 'completed':
        return {
          label: 'Completed',
          icon: CheckCircle,
          className: 'completed'
        };
      case 'processing':
        return {
          label: 'Processing',
          icon: Clock,
          className: 'processing'
        };
      case 'failed':
        return {
          label: 'Failed',
          icon: XCircle,
          className: 'failed'
        };
      default:
        return {
          label: 'Pending',
          icon: AlertCircle,
          className: 'pending'
        };
    }
  };

  const filteredDocuments = documents.filter(doc =>
    doc.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
    doc.document_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    getProcessedBy(doc).toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="documents-list-container">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-lg">Loading documents...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="documents-list-container">
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-8 max-w-md text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">Error Loading Documents</h3>
            <p className="mb-6">{error}</p>
            <button 
              onClick={fetchDocuments}
              className="docs-action-btn primary"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="documents-list-container">
      {/* Top Navigation Bar */}
      <div className="docs-top-nav">
        <div className="docs-nav-content">
          {/* Left Navigation */}
          <div className="docs-nav-left">
            <div className="docs-nav-icons">
              <button 
                onClick={() => navigate(ROUTES.DASHBOARD)}
                className="docs-nav-icon"
                title="Dashboard"
              >
                <Home size={20} />
              </button>
              <button className="docs-nav-icon active" title="Documents List">
                <List size={20} />
              </button>
              <button 
                onClick={() => navigate(ROUTES.UPLOAD)}
                className="docs-nav-icon"
                title="Upload Document"
              >
                <FileText size={20} />
              </button>
              <button className="docs-nav-icon" title="Notes">
                <Copy size={20} />
              </button>
            </div>
          </div>

          {/* Center Title */}
          <div className="docs-nav-center">
            <h1 className="docs-nav-title">Artifi Contract Analyzer</h1>
            <p className="docs-nav-subtitle">powered by <span className="font-semibold">Artifi Agentic Studio</span></p>
          </div>

          {/* Right Actions */}
          <div className="docs-nav-right">
            <button className="docs-nav-icon" title="Search">
              <Search size={20} />
            </button>
            <button className="docs-nav-icon" title="Settings">
              <Settings size={20} />
            </button>
            
            {/* Quick Actions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="docs-action-btn primary flex items-center gap-2"
              >
                <Plus size={16} />
                Quick Actions
                <ChevronDown size={16} className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
                  <div className="py-2">
                    {quickActions.map((action, index) => (
                      <button
                        key={index}
                        onClick={action.action}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-700 flex items-center gap-3 transition-colors ${
                          action.primary ? 'text-blue-400 border-b border-gray-700' : 'text-gray-300'
                        }`}
                      >
                        <action.icon size={18} />
                        <span className="font-medium">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="docs-breadcrumb">
        <div className="docs-breadcrumb-content">
          <span className="docs-breadcrumb-active">Documents</span>
          <span className="docs-breadcrumb-count">({filteredDocuments.length} total)</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Controls Bar */}
        <div className="docs-controls">
          {/* Left Controls */}
          <div className="docs-controls-left">
            {/* Search */}
            <div className="docs-search-container">
              <Search className="docs-search-icon" size={20} />
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="docs-search-input"
              />
            </div>
            
            {/* Filter Button */}
            <button className="docs-filter-btn">
              <Filter size={16} />
              <span>Filter</span>
            </button>
          </div>

          {/* Right Controls */}
          <div className="docs-controls-right">
            {/* Document Count */}
            <span className="docs-count">
              {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
            </span>
            
            {/* View Toggle */}
            <div className="docs-view-toggle">
              <button
                onClick={() => setViewMode('tiles')}
                className={`docs-view-btn ${viewMode === 'tiles' ? 'active' : ''}`}
                title="Tiles View"
              >
                <Grid size={16} />
                <span>Tiles</span>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`docs-view-btn ${viewMode === 'list' ? 'active' : ''}`}
                title="List View"
              >
                <List size={16} />
                <span>List</span>
              </button>
            </div>
          </div>
        </div>

        {/* Documents Display */}
        {filteredDocuments.length === 0 ? (
          <div className="docs-empty-state">
            <FileText className="docs-empty-icon" />
            <h3 className="docs-empty-title">
              {searchTerm ? 'No documents found' : 'No documents uploaded'}
            </h3>
            <p className="docs-empty-text">
              {searchTerm 
                ? 'Try adjusting your search terms' 
                : 'Upload your first contract document to get started'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => navigate(ROUTES.UPLOAD)}
                className="docs-action-btn primary flex items-center gap-2"
              >
                <Upload size={16} />
                <span>Upload Document</span>
              </button>
            )}
          </div>
        ) : viewMode === 'tiles' ? (
          /* Tiles View */
          <div className="docs-tiles-grid">
            {filteredDocuments.map((document) => {
              const statusInfo = getStatusInfo(document.status);
              const StatusIcon = statusInfo.icon;
              
              return (
                <div
                  key={document._id}
                  onClick={() => handleDocumentClick(document)}
                  className="docs-tile"
                >
                  {/* Header */}
                  <div className="docs-tile-header">
                    <div className="docs-tile-icon">
                      <FileText size={24} />
                    </div>
                    <div className="docs-tile-actions">
                      <div className={`docs-status-badge ${statusInfo.className}`}>
                        <StatusIcon size={12} />
                        <span>{statusInfo.label}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Action menu logic here
                        }}
                        className="docs-action-btn secondary opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <h3 className="docs-tile-title" title={document.filename}>
                      {document.filename}
                    </h3>
                    <p className="docs-tile-type">{document.document_type}</p>
                    
                    {/* Metadata */}
                    <div className="docs-tile-meta">
                      <div className="docs-tile-meta-item">
                        <User size={12} />
                        <span>{getProcessedBy(document)}</span>
                      </div>
                      <div className="docs-tile-meta-item">
                        <Calendar size={12} />
                        <span>{formatDate(document.processed_date || document.upload_date)}</span>
                      </div>
                      {document.page_count && (
                        <div className="docs-tile-meta-item">
                          <FileText size={12} />
                          <span>{document.page_count} pages</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="docs-tile-footer">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDocumentClick(document);
                      }}
                      className="docs-action-btn analyze"
                    >
                      <Eye size={14} />
                      <span>Analyze</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        // Download logic here
                      }}
                      className="docs-action-btn secondary"
                      title="Download"
                    >
                      <Download size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="docs-list-container">
            {/* Table Header */}
            <div className="docs-list-header">
              <div className="docs-list-header-grid">
                <div>Document</div>
                <div>Status</div>
                <div>Processed By</div>
                <div>Date</div>
                <div>Pages</div>
                <div>Actions</div>
              </div>
            </div>

            {/* Table Body */}
            <div className="docs-list-body">
              {filteredDocuments.map((document) => {
                const statusInfo = getStatusInfo(document.status);
                const StatusIcon = statusInfo.icon;
                
                return (
                  <div
                    key={document._id}
                    onClick={() => handleDocumentClick(document)}
                    className="docs-list-row group"
                  >
                    <div className="docs-list-row-grid">
                      {/* Document Info */}
                      <div className="docs-list-doc-info">
                        <div className="docs-list-doc-icon">
                          <FileText size={20} />
                        </div>
                        <div className="docs-list-doc-text">
                          <h3 className="docs-list-doc-title" title={document.filename}>
                            {document.filename}
                          </h3>
                          <p className="docs-list-doc-type">{document.document_type}</p>
                        </div>
                      </div>

                      {/* Status */}
                      <div>
                        <div className={`docs-status-badge ${statusInfo.className}`}>
                          <StatusIcon size={12} />
                          <span>{statusInfo.label}</span>
                        </div>
                      </div>

                      {/* Processed By */}
                      <div className="docs-list-user">
                        <User size={14} />
                        <div className="docs-list-user-text">
                          <p className="docs-list-user-name">{getProcessedBy(document)}</p>
                          <p className="docs-list-user-email">system@artifi.com</p>
                        </div>
                      </div>

                      {/* Date */}
                      <div className="docs-list-date">
                        <Calendar size={14} />
                        <span>
                          {formatDate(document.processed_date || document.upload_date)}
                        </span>
                      </div>

                      {/* Pages */}
                      <div className="docs-list-pages">
                        {document.page_count || '-'}
                      </div>

                      {/* Actions */}
                      <div className="docs-list-actions">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDocumentClick(document);
                          }}
                          className="docs-action-btn primary"
                          title="Analyze"
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Download logic here
                          }}
                          className="docs-action-btn secondary"
                          title="Download"
                        >
                          <Download size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // Action menu logic here
                          }}
                          className="docs-action-btn secondary opacity-0 group-hover:opacity-100"
                          title="More actions"
                        >
                          <MoreHorizontal size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsList;